/**
 * VIP 13 – REBOA RCT Form (13NewCase.js)
 * ------------------------------------------------------------
 * 특이사항 & 구현 규칙 (2024-10-27 작성)
 * 1. Date-Time 입력 방식
 *    • HTML5 의 <input type="datetime-local"> 사용
 *    • VIP 11과 달리 원본 HTML에서 datetime-local을 사용하므로 그대로 적용
 *    • 저장 시 ISO 형식으로 변환
 *
 * 2. hospital 필드 자동 매핑 & 잠금
 *    • /edc/user-value-check Lambda 로부터 user_value(정수) 를 가져온다.
 *    • user_value 의 10의 자리(1~9)를 알파벳(A~I)에 매핑 (10→A, 20→B … 90→I).
 *    • 매핑된 병원 코드가 formData.hospital 에 자동 세팅된다.
 *    • UI 에서 병원 라디오는 disabled 처리하여 사용자가 수정 불가.
 *
 * 3. user_value 조회
 *    • formAPI.getUserValue(projectId) 호출.
 *    • 토큰은 헤더 + body 모두 전달해 Lambda 에서 인증.
 *
 * 4. 기타
 *    • 총 7 Step Wizard 구조 (Assessment, Prehospital, ED, REBOA, After Admission, Hemodynamics, Study Completion).
 *    • 추후 필드 추가/조건부 렌더링 시 이 헤더 주석에 변경사항을 기록할 것.
 *
 * 5. Randomization 시스템
 *    • Inclusion/Exclusion 조건 충족 시 자동 표시
 *    • 조건: inclu_ohca=yes, inclu_age=yes, inclu_witnessed=witnessed, 모든 exclusion=no
 *    • 외부 API 호출로 REBOA 그룹 배정
 *
 * 6. 재사용 가능한 컴포넌트
 *    • renderRadioField: 라디오 버튼 필드 자동 생성
 *    • renderTextField: 텍스트 입력 필드 자동 생성
 *    • 코드 중복 최소화 및 유지보수성 향상
 * ------------------------------------------------------------
 */
import React, { useState, useEffect } from "react";
import { FiChevronLeft, FiChevronRight, FiSave, FiX } from "react-icons/fi";
import { formAPI } from "../../../../../services/api";
import Page1 from "./pages/1_page";
import Page2 from "./pages/2_page";
import Page3 from "./pages/3_page";
import Page4 from "./pages/4_page";
import Page5 from "./pages/5_page";
import Page6 from "./pages/6_page";
import Page7 from "./pages/7_page";
import "./12NewCase.css";
import { useNavigate, useLocation, useParams } from "react-router-dom";

// 전체 단계 개수
const totalSteps = 7;

// 단계 라벨
const stepLabels = [
  "Assessment",
  "Prehospital",
  "Emergency Department",
  "REBOA",
  "After Admission",
  "Hemodynamics",
  "Study Completion",
];

const VIP12NewCase = ({ projectId }) => {
  // 상태 관리
  const [currentStep, setCurrentStep] = useState(1);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [userValue, setUserValue] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: "", text: "" });
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showSavePopover, setShowSavePopover] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editDataId, setEditDataId] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();

  // 초기 formData 상태
  const initialFormData = {
    // Page 1: Assessment
    pt_number: "",
    pt_name: "",
    date_of_birth: "",
    age: "",
    sex: "",
    ed_arrival_datetime: "",
    height: "",
    weight: "",
    waist_circumference: "",
    hip_circumference: "",

    // Inclusion Criteria
    inclu_ohca: "",
    inclu_witnessed: "",
    inclu_age: "",

    // Exclusion Criteria
    exclu_: "", // Pregnancy
    exclu_rosc: "",
    exclu_cpc34: "",
    exclu_bleeding: "",
    exclu_aorticds: "",
    exclu_dead: "",
    exclu_ecmo: "",
    exclu_traumatic: "",
    exclu_request: "",

    // Randomization
    timeOfRandomization: "",
    reboa: "",
    confirm: "no",

    // Page 2: Prehospital
    arrest_datetime: "",
    arrest_lnt: "",
    arrest_place: "",
    baseline_cpc: "",
    arrest_witness: "",
    imm_compress: "",
    bystander_cpr: "",
    bystander_datetime: "",
    bystander_aed: "",
    ems_call_datetime: "",
    ems_arrival_datetimetime: "",
    X2nd_arrive_time: "",
    EMS_Departure_time: "",
    ems_compression: "",
    ems_compression_method: "",
    ems_initial_rhythm: "",
    ems_initial_rhythm_time: "",
    ems_initial_rhythm_obsv: "",
    ems_defib: "",
    ems_defib_num: "",
    ems_defib_energy: "",
    ems_airway: "",
    ems_airway_datetime: "",
    ems_iv: "",
    ems_iv_fluid: "",
    ems_med_epi: "",
    ems_epi_time: "",
    ems_med_amio: "",
    ems_amio_time: "",
    ems_tor: "",
    ems_tor_reason: "",

    // Page 3: Emergency Department
    ed_arrival_datetime2: "",
    transport_method: "",
    resus_room_datetime: "",
    pmhx_htn: "",
    pmhx_dm: "",
    pmxh_dl: "",
    pmhx_lc: "",
    pmhx_hf: "",
    pmhx_stroke: "",
    pmhx_caod: "",
    pmhx_pacemaker: "",
    pmhx_icd: "",
    pmhx_ckd: "",
    pmhx_malig: "",
    ed_cpr_attempt: "",
    ed_cpr_reason: "",
    ed_compression_datetime: "",
    ed_initial_rhythm: "",
    ed_intubation: "",
    ed_intubation_type: "",
    ed_intubation_datetime: "",
    med_acls_epi: "",
    med_acls_epi_time: "",
    med_acls_amio: "",
    med_acls_amio_dose: "",
    defib_datetime: "",
    defib_energy: "",
    defib_attempts: "",
    cpr_aline: "",
    cpr_aline_time: "",
    cpr_cline: "",
    cpr_cline_time: "",
    cpr_dis_time: "",
    cpr_fluid: "",
    cpr_dis_reason: "",
    cause_arrest: "",
    cause_medical: "",
    cause_trauma: "",
    cause_asphyxia: "",
    any_rosc: "",
    rosc_ed: "",
    nonsust_rosc_ed: "",
    sust_rosc_ed: "",
    rosc_ecg: "",
    ed_outcome: "",

    // Page 4: REBOA
    art_sheath_ins: "",
    art_sheath_ins_site: "",
    art_sheath_ins_attempts: "",
    cline_ins: "",
    cline_ins_site: "",
    cline_ins_attempts: "",
    abp_start_datetime: "",
    cvp_start_datetime: "",
    reboa_ins: "",
    reboa_ins_datetime: "",
    reboa_ins_depth: "",
    reboa_ins_attempts: "",
    reboa_confirm: "",
    reboa_inflation_datetime: "",
    reboa_inflation_vol: "",
    reboa_deflation_datetime: "",

    // Device Related Complications
    ae_malposition: "",
    ae_ins_fail: "",
    ae_ins_hematoma: "",
    ae_ins_infection: "",
    ae_balloon_rupture: "",
    ae_removal_failure: "",

    // Vascular Complications
    ae_fem_art_inj: "",
    ae_limb_isch: "",
    ae_amputation: "",
    ae_aorta_inj: "",

    // Other Adverse Events
    ae_aki_dialysis: "",
    ae_ards: "",
    ae_bacteremia: "",
    ae_pneumonia: "",
    ae_sepsis: "",
    ae_stroke: "",
    ae_paraplegia: "",
    ae_mi: "",
    ae_mof: "",
    ae_spinal_ische: "",
    ae_vent_days: "",
    ae_icu_stay: "",
    ae_hospital_stay: "",
    ae_hospital_mortality: "",
    ae_mortality_hours: "",
    ae_mortality_days: "",

    // Page 5: After Admission
    ttm: "",
    ttm_temp: "",
    ttm_cause: "",
    cag: "",
    pci: "",
    cabg: "",
    iabp: "",
    crrt: "",
    tcp: "",

    // Laboratory Data
    wbc: "",
    hb: "",
    plt: "",
    na: "",
    k: "",
    cl: "",
    tco2: "",
    bun: "",
    cr: "",
    tbil: "",
    ast: "",
    alt: "",
    ica: "",
    phos: "",
    alb: "",
    alp: "",
    pt: "",
    aptt: "",
    crp: "",
    ck: "",
    ckmb: "",
    troponin_i: "",
    probnp: "",
    ddimer: "",
    nse: "",
    ph: "",
    po2: "",
    pco2: "",
    hco3: "",
    lactate: "",
    sao2: "",
    scvo2: "",
    cortisol: "",
    acth: "",
    adh: "",

    // Discharge Outcome
    discharge_date: "",
    discharge_type: "",
    cpc_discharge: "",
    mrs_discharge: "",
    cpc_1month: "",
    mrs_1month: "",
    cpc_3month: "",
    mrs_3month: "",
    cpc_6month: "",
    mrs_6month: "",

    // Page 6: Hemodynamics (REBOA 전후 활력징후)
    // 2min before
    sbp_pre_2min: "",
    dbp_pre_2min: "",
    mbp_pre_2min: "",
    hr_pre_2min: "",
    rr_pre_2min: "",
    spo2_pre_2min: "",
    cvp_pre_2min: "",
    etco2_pre_2min: "",
    bt_pre_2min: "",

    // 1min before
    sbp_pre_1min: "",
    dbp_pre_1min: "",
    mbp_pre_1min: "",
    hr_pre_1min: "",
    rr_pre_1min: "",
    spo2_pre_1min: "",
    cvp_pre_1min: "",
    etco2_pre_1min: "",
    bt_pre_1min: "",

    // 1min after
    sbp_after_1min: "",
    dbp_after_1min: "",
    mbp_after_1min: "",
    hr_after_1min: "",
    rr_after_1min: "",
    spo2_after_1min: "",
    cvp_after_1min: "",
    etco2_after_1min: "",
    bt_after_1min: "",

    // 2min after
    sbp_after_2min: "",
    dbp_after_2min: "",
    mbp_after_2min: "",
    hr_after_2min: "",
    rr_after_2min: "",
    spo2_after_2min: "",
    cvp_after_2min: "",
    etco2_after_2min: "",
    bt_after_2min: "",

    // 4min after
    sbp_after_4min: "",
    dbp_after_4min: "",
    mbp_after_4min: "",
    hr_after_4min: "",
    rr_after_4min: "",
    spo2_after_4min: "",
    cvp_after_4min: "",
    etco2_after_4min: "",
    bt_after_4min: "",

    // 10min after
    sbp_after_10min: "",
    dbp_after_10min: "",
    mbp_after_10min: "",
    hr_after_10min: "",
    rr_after_10min: "",
    spo2_after_10min: "",
    cvp_after_10min: "",
    etco2_after_10min: "",
    bt_after_10min: "",

    // Page 7: Study Completion
    study_completion: "",
    withdraw_date: "",
    withdraw_reason: "",
    completion_date: "",
    others: "",

    // Status fields
    assessment_status: "Not started",
    prehospital_status: "Not started",
    ed_status: "Not started",
    reboa_status: "Not started",
    admission_status: "Not started",
    hemodynamics_status: "Not started",
    completion_status: "Not started",

    // System fields
    hospital: "",
    data_id: "",
    case_id: "",
    user_id: "",
  };

  // formData 상태
  const [formData, setFormData] = useState(initialFormData);

  // 디버깅: 컴포넌트 로드 및 formData 변경 시 전체 구조 출력
  useEffect(() => {
    console.log("=== [DEBUG] 12NewCase 컴포넌트 상태 ===");
    console.log("현재 Step:", currentStep);
    console.log("userValue:", userValue);
    console.log("projectId:", projectId);
    console.log("전체 formData 객체:", formData);
    console.log("formData 키 개수:", Object.keys(formData).length);
    console.log(
      "비어있지 않은 필드들:",
      Object.entries(formData)
        .filter(([key, value]) => {
          if (Array.isArray(value)) return value.length > 0;
          if (typeof value === "string") return value.trim() !== "";
          return value !== null && value !== undefined;
        })
        .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {})
    );
    console.log("=== [DEBUG] 12NewCase 끝 ===");
  }, [formData, currentStep, userValue]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData((p) => {
      const prevArr = Array.isArray(p[name]) ? p[name] : [];
      let newArr;
      if (checked) {
        newArr = prevArr.includes(value) ? prevArr : [...prevArr, value];
      } else {
        newArr = prevArr.filter((v) => v !== value);
      }
      return { ...p, [name]: newArr };
    });
  };

  const dtDisplay = (v) => (typeof v === "string" ? v.replace("T", " ") : v);

  // VIP11과 동일한 data_id 생성 로직
  const generateDataId = () => {
    // 현재 timestamp 생성
    const timestamp = new Date().getTime();

    // 5자리 랜덤 숫자 생성 (10000~99999)
    const randomNum = Math.floor(Math.random() * 90000) + 10000;

    // userValue-timestamp-randomNumber 형식으로 조합
    return `${userValue || "unknown"}-${timestamp}-${randomNum}`;
  };

  // Edit 모드 감지 및 기존 데이터 로드
  useEffect(() => {
    // URL에서 Edit 모드 확인 (/ecrf/subjectlist/edit/:dataId)
    if (location.pathname.includes("/edit/")) {
      const dataId = location.pathname.split("/edit/")[1];
      setIsEditMode(true);
      setEditDataId(dataId);

      // location.state에서 기존 데이터 로드
      if (location.state && location.state.formData) {
        console.log(
          "[VIP12NewCase] Edit 모드 - 기존 데이터 로드:",
          location.state.formData
        );
        setFormData(location.state.formData);
      }
    } else {
      // Edit 모드가 아닌 경우 (신규 케이스) - 강제 초기화
      setIsEditMode(false);
      setEditDataId(null);

      // 이전 Edit 모드에서 온 경우 formData 완전 초기화
      if (isEditMode) {
        console.log(
          "[VIP12NewCase] Edit → 신규 케이스 모드 전환 - 데이터 초기화"
        );
        setFormData(initialFormData);
        setCurrentStep(1);
      }
    }
  }, [location]);

  // user_value 조회
  useEffect(() => {
    if (!projectId) return;

    (async () => {
      try {
        const res = await formAPI.getUserValue(projectId);
        const val = res.userValue ?? res.user_value ?? 0;
        setUserValue(val);
      } catch (err) {
        console.error("[VIP12] getUserValue 오류:", err);
        // 에러 발생 시에도 기본값 0으로 설정하여 저장 가능하도록 함
        setUserValue(0);
      }
    })();
  }, [projectId]);

  // VIP11과 동일한 방식으로 변경 - 강제 초기화 제거

  // userValue → data_id & case_id 동시 생성 (Edit 모드가 아닐 때만)
  useEffect(() => {
    if (userValue == null) return;

    // 관리자(999)는 ID 생성하지 않음
    if (userValue === 999) return;

    // Edit 모드에서는 ID 생성하지 않음 (기존 데이터 사용)
    if (isEditMode) return;

    // 새로운 케이스일 때만 고유 ID 생성
    if (!formData.data_id && !formData.case_id) {
      // 동일한 timestamp와 randomNumber 사용
      const timestamp = new Date().getTime();
      const randomNum = Math.floor(Math.random() * 90000) + 10000;
      const uniqueId = `${userValue}-${timestamp}-${randomNum}`;

      console.log("[VIP12NewCase] 새로운 고유 ID 생성:", uniqueId);

      setFormData((p) => ({
        ...p,
        data_id: uniqueId, // 화면 표시용
        case_id: uniqueId, // 저장 식별용 (동일한 값)
        user_id: userValue.toString(),
      }));
    }
  }, [userValue, isEditMode]);

  // 스크롤 이벤트 리스너
  useEffect(() => {
    const handleScroll = () => {
      const ecrfContent = document.querySelector(".ecrf-content");
      const scrollTop = ecrfContent ? ecrfContent.scrollTop : window.scrollY;
      setShowScrollButton(scrollTop > 300);
    };

    const ecrfContent = document.querySelector(".ecrf-content");
    const targetElement = ecrfContent || window;

    targetElement.addEventListener("scroll", handleScroll);
    return () => targetElement.removeEventListener("scroll", handleScroll);
  }, []);

  // 맨 위로 스크롤
  const goTop = () => {
    const ecrfContent = document.querySelector(".ecrf-content");

    if (ecrfContent) {
      ecrfContent.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    } else {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }

    setTimeout(() => {
      if (document.querySelector(".vip-new-case")) {
        document.querySelector(".vip-new-case").focus();
      }
    }, 100);
  };

  // 관리자 접근 제한 메시지 컴포넌트
  const AdminRestrictedMessage = () => (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        zIndex: 100,
      }}
    >
      <div
        style={{
          backgroundColor: "#f8f9fa",
          padding: "2rem",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          textAlign: "center",
          maxWidth: "400px",
        }}
      >
        <div
          style={{
            fontSize: "64px",
            color: "#dc3545",
            marginBottom: "1rem",
          }}
        >
          ⚠️
        </div>
        <h2 style={{ color: "#dc3545", marginBottom: "1rem" }}>접근 제한</h2>
        <p style={{ color: "#666", lineHeight: "1.5" }}>
          관리자 계정으로는 새로운 케이스를 생성할 수 없습니다.
          <br />
          일반 계정으로 로그인해 주세요.
        </p>
      </div>
    </div>
  );

  // 저장 메시지 표시 컴포넌트
  const SaveMessage = () => {
    if (!saveMessage.text) return null;

    const bgColor = saveMessage.type === "success" ? "#4CAF50" : "#f44336";

    return (
      <div
        style={{
          position: "fixed",
          top: "80px",
          right: "20px",
          backgroundColor: bgColor,
          color: "white",
          padding: "12px 24px",
          borderRadius: "4px",
          boxShadow: "0 2px 5px rgba(0,0,0,0.2)",
          zIndex: 1000,
          animation: "fadeIn 0.3s ease",
        }}
      >
        {saveMessage.text}
      </div>
    );
  };

  // 성공 팝업 컴포넌트
  const SuccessPopup = () => {
    if (!showSuccessPopup) return null;

    return (
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 1100,
        }}
      >
        <div
          style={{
            backgroundColor: "white",
            padding: "30px",
            borderRadius: "8px",
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.1)",
            textAlign: "center",
            minWidth: "300px",
          }}
        >
          <h3 style={{ margin: "0 0 20px 0", color: "#1976D2" }}>
            {isEditMode ? "Update Complete" : "Save Complete"}
          </h3>
          <p style={{ margin: "0 0 20px 0" }}>
            {isEditMode
              ? "Data has been successfully updated."
              : "Data has been successfully saved."}
          </p>
          <button
            onClick={() => {
              setShowSuccessPopup(false);
              navigate("/ecrf/subjectlist");
            }}
            style={{
              backgroundColor: "#1976D2",
              color: "white",
              border: "none",
              padding: "10px 20px",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            OK
          </button>
        </div>
      </div>
    );
  };

  // 저장 핸들러
  const handleSave = async () => {
    if (isSaving) return;

    setIsSaving(true);
    setSaveMessage({ type: "", text: "" });

    try {
      // REBOA RCT: userValue와 관계없이 누구나 데이터 전송 가능
      // SubjectList 접근 제한은 별도로 처리

      // 빈 값을 제외한 데이터 객체 생성
      const filteredFormData = Object.entries(formData).reduce(
        (acc, [key, value]) => {
          // 배열인 경우 길이가 0이면 제외
          if (Array.isArray(value) && value.length === 0) {
            return acc;
          }
          // 문자열인 경우 빈 문자열이면 제외
          if (typeof value === "string" && value.trim() === "") {
            return acc;
          }
          // null 또는 undefined면 제외
          if (value === null || value === undefined) {
            return acc;
          }
          // 위 조건을 통과한 경우만 포함
          return { ...acc, [key]: value };
        },
        {}
      );

      // VIP 11과 동일한 방식으로 formAPI.sendVipData 사용
      const result = await formAPI.sendVipData(
        filteredFormData,
        projectId,
        formData.case_id || formData.data_id || ""
      );

      if (result.error) {
        throw new Error(result.error);
      }

      // 성공 팝업 표시
      setShowSuccessPopup(true);
    } catch (error) {
      console.error("저장 실패:", error);

      // 에러 메시지 표시
      setSaveMessage({
        type: "error",
        text: `저장 실패: ${
          error.message || "알 수 없는 오류가 발생했습니다."
        }`,
      });
    } finally {
      setIsSaving(false);
    }
  };

  // renderFixedPanel 함수
  const renderFixedPanel = () => {
    // 상태에 따른 배경색 결정 함수
    const getStatusColor = (status) => {
      switch (status) {
        case "Complete":
          return "#4CAF50"; // 초록색
        case "In process":
          return "#FFA726"; // 주황색
        case "Not started":
          return "#EF5350"; // 빨간색
        default:
          return "#9E9E9E"; // 회색
      }
    };

    // 상태 표시 컴포넌트
    const StatusItem = ({ label, status }) => (
      <div
        className="status-item"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 16px",
          borderBottom: "1px solid #eee",
        }}
      >
        <span
          className="status-label"
          style={{
            fontWeight: "500",
            color: "#333",
            flex: "0 0 200px",
          }}
        >
          {label}:
        </span>
        <span
          className="status-value"
          style={{
            backgroundColor: getStatusColor(status),
            color: "white",
            padding: "4px 12px",
            borderRadius: "12px",
            fontWeight: "bold",
            minWidth: "100px",
            textAlign: "center",
            display: "inline-block",
          }}
        >
          {status || "미입력"}
        </span>
      </div>
    );

    return (
      <div
        className={`fixed-bottom-panel ${
          isPanelExpanded ? "expanded" : "collapsed"
        }`}
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: "white",
          boxShadow: "0 -2px 10px rgba(0,0,0,0.1)",
          zIndex: 1000,
          transition: "all 0.3s ease",
          maxHeight: isPanelExpanded ? "70vh" : "48px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          className="panel-header"
          onClick={() => setIsPanelExpanded(!isPanelExpanded)}
          style={{
            padding: "12px 24px",
            backgroundColor: "#1976D2",
            color: "white",
            cursor: "pointer",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: "48px",
          }}
        >
          <span
            className="panel-title"
            style={{ fontWeight: "bold", fontSize: "16px" }}
          >
            Status 체크
          </span>
          <span
            className="panel-toggle"
            style={{
              transform: isPanelExpanded ? "rotate(180deg)" : "",
              transition: "transform 0.3s ease",
            }}
          >
            ▼
          </span>
        </div>
        {isPanelExpanded && (
          <div
            className="panel-content"
            style={{
              padding: "0",
              overflowY: "auto",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
              gap: "0",
              maxHeight: "calc(70vh - 48px)",
            }}
          >
            <div
              className="status-grid"
              style={{ display: "flex", flexDirection: "column" }}
            >
              <StatusItem
                label="Assessment"
                status={formData.assessment_status}
              />
              <StatusItem
                label="Prehospital"
                status={formData.prehospital_status}
              />
              <StatusItem
                label="Emergency Department"
                status={formData.ed_status}
              />
              <StatusItem label="REBOA" status={formData.reboa_status} />
              <StatusItem
                label="After Admission"
                status={formData.admission_status}
              />
              <StatusItem
                label="Hemodynamics"
                status={formData.hemodynamics_status}
              />
              <StatusItem
                label="Study Completion"
                status={formData.completion_status}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  // renderStep 함수
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <Page1
              formData={formData}
              handleChange={handleChange}
              handleCheckboxChange={handleCheckboxChange}
              dtDisplay={dtDisplay}
            />
          </>
        );
      case 2:
        return (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <Page2
              formData={formData}
              handleChange={handleChange}
              handleCheckboxChange={handleCheckboxChange}
              dtDisplay={dtDisplay}
            />
          </>
        );
      case 3:
        return (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <Page3
              formData={formData}
              handleChange={handleChange}
              handleCheckboxChange={handleCheckboxChange}
              dtDisplay={dtDisplay}
            />
          </>
        );
      case 4:
        return (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <Page4
              formData={formData}
              handleChange={handleChange}
              handleCheckboxChange={handleCheckboxChange}
              dtDisplay={dtDisplay}
            />
          </>
        );
      case 5:
        return (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <Page5
              formData={formData}
              handleChange={handleChange}
              handleCheckboxChange={handleCheckboxChange}
              dtDisplay={dtDisplay}
            />
          </>
        );
      case 6:
        return (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <Page6
              formData={formData}
              handleChange={handleChange}
              handleCheckboxChange={handleCheckboxChange}
              dtDisplay={dtDisplay}
            />
          </>
        );
      case 7:
        return (
          <>
            <div className="data_id_container">
              <div className="data_id_value">{formData.data_id}</div>
            </div>
            <Page7
              formData={formData}
              handleChange={handleChange}
              handleCheckboxChange={handleCheckboxChange}
              dtDisplay={dtDisplay}
            />
          </>
        );
      default:
        return (
          <section className="vip-form-section">
            <h2 className="section-title">페이지를 찾을 수 없습니다</h2>
          </section>
        );
    }
  };

  // StepIndicator 컴포넌트
  const StepIndicator = () => (
    <div className="vip-step-header">
      {/* Edit 모드 표시 */}
      {isEditMode && (
        <div
          style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "4px",
            padding: "8px 16px",
            marginBottom: "16px",
            textAlign: "center",
            color: "#856404",
            fontWeight: "bold",
          }}
        >
          📝 Edit mode
        </div>
      )}

      <div className="vip-step-indicator">
        {stepLabels.map((label, index) => (
          <div
            key={index}
            className={`step-item ${
              currentStep === index + 1 ? "active" : ""
            } ${currentStep > index + 1 ? "completed" : ""}`}
            onClick={() => setCurrentStep(index + 1)}
          >
            <div className="step-number">{index + 1}</div>
            <div className="step-label">{label}</div>
          </div>
        ))}
      </div>
    </div>
  );

  // StepActions 컴포넌트
  const StepActions = () => (
    <div className="vip-step-actions">
      <div className="navigation-buttons">
        <button
          type="button"
          className={`vip-step-button prev ${
            currentStep === 1 ? "disabled" : ""
          }`}
          onClick={() => setCurrentStep((p) => Math.max(1, p - 1))}
          disabled={currentStep === 1}
        >
          <FiChevronLeft size={16} />
          이전
        </button>

        <button
          type="button"
          className={`vip-step-button next ${
            currentStep === totalSteps ? "disabled" : ""
          }`}
          onClick={() => setCurrentStep((p) => Math.min(totalSteps, p + 1))}
          disabled={currentStep === totalSteps}
        >
          다음
          <FiChevronRight size={16} />
        </button>
      </div>

      <div className="save-button-container">
        <button
          type="button"
          className={`vip-submit-button large ${isSaving ? "disabled" : ""}`}
          onClick={handleSave}
          disabled={isSaving}
        >
          <FiSave size={16} />
          {isSaving
            ? isEditMode
              ? "Updating..."
              : "Saving..."
            : isEditMode
            ? "Update"
            : "Save"}
        </button>
      </div>
    </div>
  );

  // 저장 팝오버 컴포넌트
  const SavePopover = () => {
    if (!showSavePopover) return null;

    return (
      <div
        style={{
          position: "fixed",
          right: "80px",
          top: "170px",
          backgroundColor: "white",
          padding: "15px",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
          zIndex: 1100,
          width: "200px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px",
          }}
        >
          <span style={{ fontWeight: "bold" }}></span>
          <button
            onClick={() => setShowSavePopover(false)}
            style={{
              border: "none",
              background: "none",
              padding: "4px",
              cursor: "pointer",
              color: "#666",
            }}
          >
            <FiX size={16} />
          </button>
        </div>
        <button
          onClick={() => {
            handleSave();
            setShowSavePopover(false);
          }}
          disabled={isSaving}
          style={{
            width: "100%",
            padding: "8px",
            backgroundColor: "#1976D2",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          <FiSave size={16} />
          {isSaving
            ? isEditMode
              ? "Updating..."
              : "Saving..."
            : isEditMode
            ? "Update"
            : "Save"}
        </button>
      </div>
    );
  };

  // 떠다니는 저장 아이콘
  const FloatingSaveButton = () => (
    <button
      onClick={() => setShowSavePopover(!showSavePopover)}
      style={{
        position: "fixed",
        right: "20px",
        top: "80px",
        width: "50px",
        height: "50px",
        borderRadius: "50%",
        backgroundColor: "#1976D2",
        color: "white",
        border: "none",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        zIndex: 1000,
      }}
    >
      <FiSave size={24} />
    </button>
  );

  return (
    <div
      className="vip-new-case"
      style={{ position: "relative", minHeight: "100vh" }}
    >
      {/* REBOA RCT: 모든 사용자가 새 케이스 생성 가능 */}
      <SaveMessage />
      <SuccessPopup />
      <FloatingSaveButton />
      <SavePopover />
      <StepIndicator />
      {renderStep()}
      <StepActions />
      {renderFixedPanel()}

      {/* 맨 위로 이동 버튼 */}
      {showScrollButton && (
        <button
          onClick={goTop}
          style={{
            position: "fixed",
            right: "24px",
            bottom: "24px",
            width: "50px",
            height: "50px",
            borderRadius: "50%",
            backgroundColor: "#007bff",
            color: "#fff",
            border: "none",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "20px",
            zIndex: 1000,
            marginBottom: "200px",
          }}
        >
          ↑
        </button>
      )}
    </div>
  );
};

export default VIP12NewCase;
